import { API } from '@aws-amplify/api';
import { Role } from '../../@types/auth';
import { CognitoUser, UserListUser } from '../../@types/user';
import getRole from '../getRole';

// Safety valve only. The backend accumulates >= 1500 users per response, so the
// pool would have to exceed ~75k identities to reach this. It exists so a bug in
// the backend's token handling cannot spin the browser forever.
const MAX_PAGES_TO_FETCH = 50;

export interface GetUserListInput {
  /**
   * Called after each page with everything fetched so far, so a caller can render
   * progressively instead of waiting for all ~13 sequential requests.
   *
   * Opt-in: callers that need the complete set before they can do anything
   * useful (the edit page resolves one user out of the list, and would show
   * "not found" while the list is still filling) simply omit it.
   */
  onProgress?: (usersSoFar: UserListUser[]) => void;
}

function toUserListUser(user: CognitoUser): UserListUser {
  const group = user.groups?.length > 0 ? user.groups[0] : 0;
  const outputUser: UserListUser = {
    name: user.name,
    email: user.email,
    image: user.image,
    verified: user.verified,
    group,
    level: user.level,
    campusId: user.campusId,
    role: getRole(group?.toString()),
    studentId: undefined,
    studentIds: undefined,
    // Cognito's Enabled flag. Absent means enabled, matching Cognito's own
    // behaviour of omitting it for active accounts.
    enabled: user.enabled ?? true,
  };

  if (user.studentIds.length > 0) {
    outputUser.studentIds = user.studentIds;

    if (outputUser.role === Role.STUDENT && user.studentIds[0] !== '%') {
      outputUser.studentId = user.studentIds[0];
    }
  }

  return outputUser;
}

export async function getUserList(input?: GetUserListInput): Promise<UserListUser[]> {
  try {
    const apiName = process.env.NEXT_PUBLIC_DASH_API_NAME!;
    const path = 'get-user-list';
    let token: string | undefined = undefined;
    const users: UserListUser[] = [];
    let pages = 0;
    // Continue purely on the presence of a continuation token. The previous
    // condition stopped unless a response held exactly 1500 users, but the
    // backend fills its batch to *at least* 1500 and returns whatever the final
    // Cognito page pushed it to (1501-1559 in practice), so the loop exited on
    // the first response and silently dropped every later user.
    do {
      const response: { users: CognitoUser[]; nextToken: string | undefined } = await API.get(
        apiName,
        path,
        { queryStringParameters: { nextToken: token } }
      );
      users.push(...response.users.map(toUserListUser));
      token = response.nextToken;
      pages += 1;
      // A fresh array each time: the caller stores this in state, and handing it
      // the same mutable reference would not re-render.
      input?.onProgress?.([...users]);
    } while (token && pages < MAX_PAGES_TO_FETCH);

    return users;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
